import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Landmark as LandmarkIcon, MapPinOff, MountainSnow, Route as RouteIcon } from "lucide-react";
import { TRIP_JOIN_MIN_DRAW_M, type TripShareData } from "@atlas/shared";
import { fetchTripShare } from "../api/trips";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { FeatureSummaryCard, type SelectedFeature } from "../components/FeatureSummaryCard";
import { TripMapLegend, TripRouteMap, type TripMapPinClick } from "../components/map/TripRouteMap";
import { usePageMeta } from "../hooks/usePageMeta";
import { applySiteTheme } from "../lib/theme";
import { formatGapDistance, formatTripDates, formatTripDistance, formatTripElevation, plural } from "../lib/trip-format";

// Public trip page behind a signed share link (spec 35; bare root route, no
// shell). Same shape as the public activity page: full-screen map, glass
// panel, one gold "Join on Atlas" CTA. The owner can switch the link off, so a
// 404 here is normal. Never prerendered (signed, unlisted).

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      <span style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-.02em", whiteSpace: "nowrap" }}>
        {value}
      </span>
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--text-muted)", marginTop: 4 }}>
        {label}
      </span>
    </div>
  );
}

function Chip({ children, tone, onClick }: { children: React.ReactNode; tone: "gold" | "sky" | "spruce"; onClick: () => void }) {
  const dot = tone === "gold" ? "var(--gold-400)" : tone === "sky" ? "var(--status-info)" : "var(--status-success)";
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        padding: "5px 10px",
        borderRadius: "var(--radius-pill)",
        background: "var(--surface-raised)",
        border: "1px solid var(--border-subtle)",
        color: "var(--text-primary)",
        font: "inherit",
        fontSize: 12.5,
        fontWeight: 600,
        cursor: "pointer",
        maxWidth: "100%",
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: 999, background: dot, flexShrink: 0 }} />
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{children}</span>
    </button>
  );
}

function Panel({ data, onSelect }: { data: TripShareData; onSelect: (f: SelectedFeature) => void }) {
  const { trip, owner } = data;
  const peakIds = new Set(data.peaks.map((p) => p.id));
  const landmarks = data.landmarks.filter((l) => !(l.category === "trig_point" && l.peakId && peakIds.has(l.peakId)));
  const hasReached = data.peaks.length + landmarks.length + data.trails.length > 0;
  // Spec 38: distance includes stretches filled from the planned route.
  const filledM = trip.filledM ?? 0;
  const stats: Array<[string, string]> = [
    [formatTripDistance(trip.distanceM + filledM), "Distance"],
    [formatTripElevation(trip.elevationGainM), "Ascent"],
    [String(trip.days), trip.days === 1 ? "Day" : "Days"],
  ];
  if (trip.nights > 0) stats.push([String(trip.nights), trip.nights === 1 ? "Night out" : "Nights out"]);

  return (
    <div
      className="atlas-glass trip-share-panel"
      style={{
        position: "absolute",
        left: 16,
        top: 16,
        zIndex: 10,
        width: 390,
        maxWidth: "calc(100vw - 32px)",
        maxHeight: "calc(100vh - 32px)",
        overflowY: "auto",
        borderRadius: "var(--radius-xl)",
        boxShadow: "var(--shadow-lg)",
        padding: 20,
        display: "flex",
        flexDirection: "column",
        gap: 16,
        minWidth: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <a href="/" style={{ display: "inline-flex", alignItems: "center", textDecoration: "none" }}>
          <img src="/atlas-logo.svg" alt="Atlas" height={26} style={{ display: "block" }} />
        </a>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, letterSpacing: ".08em", color: "var(--text-secondary)" }}>
          {formatTripDates(trip.startedAt, trip.endedAt)}
        </span>
      </div>

      <div>
        <div className="eyebrow" style={{ fontSize: 10.5, marginBottom: 6 }}>Trip</div>
        <h1 style={{ font: "var(--type-display)", fontSize: 24, lineHeight: 1.15, letterSpacing: "-.02em", color: "var(--text-primary)", margin: 0, overflowWrap: "anywhere" }}>
          {trip.name}
        </h1>
        {owner && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
            {owner.avatarUrl ? (
              <img src={owner.avatarUrl} alt="" width={30} height={30} style={{ borderRadius: 999, objectFit: "cover", border: "2px solid var(--gold-500)" }} />
            ) : (
              <span style={{ width: 30, height: 30, borderRadius: 999, background: "var(--surface-raised)", border: "2px solid var(--gold-500)", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 700, color: "var(--text-primary)" }}>
                {owner.name.split(/\s+/).map((w) => w[0] ?? "").join("").slice(0, 2).toUpperCase() || "A"}
              </span>
            )}
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>
              {owner.username ? <a href={`/explorers/${owner.username}`} style={{ color: "inherit", textDecoration: "none" }}>{owner.name}</a> : owner.name}
            </span>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700, letterSpacing: ".06em", padding: "2px 8px", borderRadius: 999, background: "var(--gold-500)", color: "var(--text-on-gold)" }}>
              LVL {owner.level}
            </span>
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: `repeat(${stats.length}, auto)`, justifyContent: "space-between", gap: 12 }}>
        {stats.map(([v, l]) => (
          <Stat key={l} value={v} label={l} />
        ))}
      </div>

      {filledM > 0 && (
        <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.45 }}>
          {formatTripDistance(filledM)} of it from the planned route, filling gaps between recordings. Shown dotted.
        </p>
      )}
      {trip.unrecordedM > 0 && (
        <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.45 }}>
          {formatGapDistance(trip.unrecordedM)} between recordings is shown dashed and isn&#39;t counted.
        </p>
      )}

      {hasReached && (
        <div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8 }}>
            Reached on this trip
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {data.peaks.map((p) => (
              <Chip key={p.id} tone="gold" onClick={() => onSelect({ kind: "peak", slug: p.slug, name: p.name })}>
                <MountainSnow size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                {p.name}
                {p.elevationM != null ? ` · ${Math.round(p.elevationM)} m` : ""}
                {p.isNew ? " · new" : ""}
              </Chip>
            ))}
            {landmarks.map((l) => (
              <Chip key={l.id} tone="sky" onClick={() => onSelect({ kind: "landmark", slug: l.slug, name: l.name })}>
                <LandmarkIcon size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                {l.name}
                {l.isNew ? " · new" : ""}
              </Chip>
            ))}
            {data.trails.map((t) => (
              <Chip key={t.id} tone="spruce" onClick={() => onSelect({ kind: "trail", slug: t.slug, name: t.name })}>
                <RouteIcon size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                {t.name} · {t.kmWalked.toFixed(1)} km
              </Chip>
            ))}
          </div>
        </div>
      )}

      <div>
        <Button
          onClick={() => {
            window.location.href = "/signup";
          }}
        >
          Join on Atlas
        </Button>
      </div>
      <p style={{ margin: 0, fontSize: 11.5, color: "var(--text-muted)", lineHeight: 1.45 }}>
        Atlas turns your GPS tracks into a record of every peak, trail and landmark you&#39;ve reached.
      </p>
    </div>
  );
}

export default function TripSharePage({ id, sig, identity }: { id: string; sig?: string; identity: boolean }) {
  const hasLink = !!sig;
  const query = useQuery({
    queryKey: ["trip-share", id, sig, identity],
    queryFn: () => fetchTripShare(id, sig!, identity),
    enabled: hasLink,
    staleTime: 60_000,
    retry: 1,
  });
  const data = query.data;
  const [wide] = React.useState(() => typeof window === "undefined" || window.innerWidth > 760);
  const [selected, setSelected] = React.useState<SelectedFeature | null>(null);
  const onPinClick = React.useCallback((pin: TripMapPinClick) => {
    setSelected({ kind: pin.kind, slug: pin.slug, name: pin.name });
  }, []);

  usePageMeta({
    title: data ? `${data.trip.name}, a trip on Atlas` : "Shared trip",
    description: data
      ? `${data.owner ? data.owner.name : "An Atlas explorer"} logged ${data.trip.name} on Atlas: ${plural(data.trip.days, "day")}, ${formatTripDistance(data.trip.distanceM + (data.trip.filledM ?? 0))}.`
      : "A shared trip on Atlas.",
    canonicalPath: `/share/trip/${id}`,
    enabled: !!data,
  });

  React.useEffect(() => {
    applySiteTheme("dark");
  }, []);

  if (!hasLink || query.isError) {
    return (
      <div style={{ height: "100vh", display: "grid", placeItems: "center", background: "var(--bg-app)", padding: 20 }}>
        <EmptyState
          icon={<MapPinOff size={20} />}
          title="This share link isn't available"
          hint="The explorer may have switched it off. Ask them for a fresh link."
        />
      </div>
    );
  }

  const joins = data?.joins ?? [];
  const fills = data?.fills ?? [];
  return (
    <div style={{ position: "relative", height: "100vh", overflow: "hidden", background: "var(--bg-app)" }}>
      <style>{`
        @media (max-width: 760px) {
          .trip-share-panel { left: 8px !important; right: 8px !important; top: auto !important; bottom: 8px !important;
            width: auto !important; max-width: none !important; max-height: 62vh !important; padding: 16px !important; }
          .trip-share-legend { display: none !important; }
        }
      `}</style>
      {data && (
        <TripRouteMap
          tracks={data.tracks}
          joins={joins}
          fills={fills}
          peaks={data.peaks}
          landmarks={data.landmarks}
          padding={
            wide
              ? { top: 48, right: 48, bottom: 48, left: 450 }
              : { top: 32, right: 32, bottom: Math.round(window.innerHeight * 0.62) + 24, left: 32 }
          }
          onPinClick={onPinClick}
        />
      )}
      {data && (
        <TripMapLegend
          className="trip-share-legend"
          corner="bottom-right"
          days={data.trip.days}
          hasJoins={joins.some((j) => !j.filled && j.distanceM >= TRIP_JOIN_MIN_DRAW_M)}
          hasCamps={joins.some((j) => j.overnight)}
          hasFills={fills.length > 0}
        />
      )}
      {data && <Panel data={data} onSelect={setSelected} />}
      {data && selected && <FeatureSummaryCard selected={selected} onClose={() => setSelected(null)} />}
      {!data && (
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "var(--text-muted)", fontSize: 13 }}>
          Loading route…
        </div>
      )}
    </div>
  );
}
